/**
 * ActionRow component renders a row of action controls for a table, including search, CSV export, filters, and a create modal button.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.page - Page configuration for toggling UI elements.
 * @param {Object} [props.searchOptions] - Search input value and handler.
 * @param {Function} [props.handleExportCSV] - Function to export data as CSV.
 * @param {Object} [props.filterOptions] - Options for client-side filters.
 * @param {Object} [props.filterDbOptions] - Options for server-side filters.
 * @param {Function} props.setNoDbFilters - Setter for non-database filters.
 * @param {Object} [props.btnCrtModalOptions] - Modal button options and handlers.
 * @returns {JSX.Element} Action row UI.
 *
 * @example
 * <ActionRow
 *   page={{ showSearch: true, showCSV: true, showBtn: true }}
 *   searchOptions={{ searchText: '', handleSearch: () => {} }}
 *   handleExportCSV={() => {}}
 *   filterOptions={{}}
 *   filterDbOptions={{}}
 *   setNoDbFilters={() => {}}
 *   btnCrtModalOptions={{
 *     btnLabelCrt: 'Create',
 *     modalSize: 'md',
 *     modalTitle: 'Add New',
 *     modalBtnLabel: 'Save',
 *     modalAdd: false,
 *     toggleAddModal: () => {},
 *     handleNewClick: () => {},
 *     handleSubmit: () => {},
 *     modalContent: <div>Modal Content</div>
 *   }}
 * />
 */

import { Row, Col, FormGroup, Label, Input } from "reactstrap";
import { FilterForm } from "./FilterForm";
import { BtnModal } from "../../components/Table/BtnModal";
import { Search } from "react-bootstrap-table2-toolkit";
import { Notification } from "../Notifications/notifications";
import { InfoPopover } from "../../pages/Priority/InfoPopover";
export const ActionRow = ({
  page,
  notificationSettings = null,
  processedData = null,
  myOrdersOptions = null,
  searchOptions = null,
  handleExportCSV = null,
  filterOptions = null,
  filterDbOptions = null,
  setNoDbFilters,
  btnCrtModalOptions = null,
  btnCrtScndModalOptions = null,
  btnCrtThrdModalOptions = null,
  infoPopoverOptions = null,
  isBootstrap,
}) => {
  const { pg, showSearch, showCSV, showInTblFltr, showDbFilter, showSwitch = false, showBtn, showScndBtn = false, showThrdBtn = false, props = {} } = page || {};
  const { showNotification, keyword, module } = notificationSettings || {};
  const infoPopoverList = Array.isArray(infoPopoverOptions) ? infoPopoverOptions : infoPopoverOptions ? [infoPopoverOptions] : [];

  const showInfoPopover = infoPopoverList.length > 0;
  const showActionTools = showCSV || showNotification || showInfoPopover;
  const showFilters = showInTblFltr || showDbFilter;

  const controls = [showSearch, showActionTools, showSwitch, showFilters, showThrdBtn, showScndBtn, showBtn];
  const visibleCount = controls.filter(Boolean).length;
  let dynamicSizes = [];
  if (visibleCount === 1) {
    dynamicSizes = [0, 0, 0, 0, 0, 0, 12];
  } else {
    dynamicSizes = [1, 1, 1, 4, 1, 2, 2];
  }
  const { isChecked, setChecked, showAllOrders, access = null } = myOrdersOptions || {};
  const { searchText, handleSearch } = searchOptions || {};
  const {
    btnLabelCrt,
    btnClass = "",
    btnColor = "primary",
    btnIcon = "mdi-plus-circle-outline",
    modalSize,
    modalTitle,
    modalBtnLabel,
    modalBtnLabelDisplay,
    modalAdd,
    toggleAddModal,
    handleNewClick,
    handleSubmit = null,
    modalContent,
    errorMsg,
  } = btnCrtModalOptions || {};
  const {
    btnLabelCrtScnd,
    btnColorScnd = "primary",
    btnIconScnd = "mdi-plus-circle-outline",
    modalSizeScnd,
    modalTitleScnd,
    modalBtnLabelScnd,
    modalAddScnd,
    toggleAddModalScnd,
    handleNewClickScnd,
    handleSubmitScnd,
    modalContentScnd,
    errorMsgScnd,
  } = btnCrtScndModalOptions || {};
  const {
    btnLabelCrtThrd,
    btnColorThrd = "primary",
    btnIconThrd = "mdi-plus-circle-outline",
    modalSizeThrd,
    modalTitleThrd,
    modalBtnLabelThrd,
    modalAddThrd,
    toggleAddModalThrd,
    handleNewClickThrd,
    handleSubmitThrd,
    modalContentThrd,
    errorMsgThrd,
  } = btnCrtThrdModalOptions || {};
  const { SearchBar } = Search;
  return (
    <Row className="ps-3 mt-2 mb-3">
      <Col md={dynamicSizes.shift()} sm="12" className="ps-0 pe-0">
        {showSearch && (
          <div className="search-box d-inline-block custom-search">
            <div className="position-relative">
              {!isBootstrap ? (
                <>
                  <input type="text" placeholder="Search..." value={searchText} onChange={(e) => handleSearch(e.target.value)} className="search-input" />
                  <i className="bx bx-search-alt search-icon" />
                </>
              ) : (
                <>
                  <SearchBar className="search-input" {...props.searchProps} />
                  <i className="bx bx-search-alt search-icon" />
                </>
              )}
            </div>
          </div>
        )}
      </Col>
      <Col md={dynamicSizes.shift()} sm="12" className="d-flex align-items-center ps-0 pe-0 gap-2" style={{ overflow: "visible" }}>
        {showCSV && (
          <button type="button" className="btn btn-success btn-label btn-m font-12 me-2" onClick={handleExportCSV}>
            <i className="mdi mdi-file-export-outline label-icon"></i> Export
          </button>
        )}

        {showInfoPopover && (
          <div
            className="d-flex align-items-center gap-3 me-1"
            style={{
              overflow: "visible",
              flexShrink: 0,
            }}
          >
            {infoPopoverList.map((item, index) => (
              <InfoPopover
                key={`${item.boardKey}-${index}`}
                isAdmin={item.isAdmin || false}
                boardKey={item.boardKey}
                adminOnly={item.adminOnly || false}
                boardImageType={item.boardImageType || "default"}
                notify={item.notify || false}
                userId={item.userId || null}
              />
            ))}
          </div>
        )}

        {showNotification && (
          <div
            className="d-flex align-items-center ms-1"
            style={{
              position: "relative",
              zIndex: 5,
              flexShrink: 0,
            }}
          >
            <Notification compData={processedData} keyWord={keyword} module={module} />
          </div>
        )}
      </Col>
      <Col md={dynamicSizes.shift()} className="d-flex flex-wrap align-content-end ps-0 pe-0 text-sm-start">
        {showSwitch && access !== false && (
          <FormGroup className="ps-0 form-switch form-switch-lg form-group">
            <Label for="myOrders"> My Orders </Label>
            <Input name="myOrders" onChange={(e) => showAllOrders(e)} className="ms-4" id="myOrders" type="checkbox" checked={isChecked} />
          </FormGroup>
        )}
      </Col>
      <Col md={dynamicSizes.shift()} sm="12" className="ps-0 pe-0" style={{ zIndex: 4 }}>
        {(showInTblFltr || showDbFilter) && <FilterForm page={page} filterOptions={filterOptions} filterDbOptions={filterDbOptions} setNoDbFilters={setNoDbFilters} />}
      </Col>
      <Col md={dynamicSizes.shift()} className="ps-0 pe-0">
        {showThrdBtn && (
          <BtnModal
            btnIcon={btnIconThrd}
            btnColor={btnColorThrd}
            modalSize={modalSizeThrd}
            btnLabel={btnLabelCrtThrd}
            modalTitle={modalTitleThrd}
            modalBtnLabel={modalBtnLabelThrd}
            modalOpen={modalAddThrd}
            toggleModal={toggleAddModalThrd}
            handleNewClick={handleNewClickThrd}
            handleSubmit={handleSubmitThrd}
            errorMsg={errorMsgThrd}
          >
            {modalContentThrd}
          </BtnModal>
        )}
      </Col>
      <Col md={dynamicSizes.shift()} className="ps-0 pe-0 text-sm-end">
        {showScndBtn && (
          <BtnModal
            btnIcon={btnIconScnd}
            btnColor={btnColorScnd}
            modalSize={modalSizeScnd}
            btnLabel={btnLabelCrtScnd}
            modalTitle={modalTitleScnd}
            modalBtnLabel={modalBtnLabelScnd}
            modalOpen={modalAddScnd}
            toggleModal={toggleAddModalScnd}
            handleNewClick={handleNewClickScnd}
            handleSubmit={handleSubmitScnd}
            errorMsg={errorMsgScnd}
          >
            {modalContentScnd}
          </BtnModal>
        )}
      </Col>
      <Col md={dynamicSizes.shift()} className="ps-0 text-sm-end">
        {showBtn && (
          <BtnModal
            btnIcon={btnIcon}
            btnColor={btnColor}
            modalSize={modalSize}
            btnClass={btnClass}
            btnLabel={btnLabelCrt}
            modalTitle={modalTitle}
            modalBtnLabel={modalBtnLabel}
            modalBtnLabelDisplay={modalBtnLabelDisplay}
            modalOpen={modalAdd}
            toggleModal={toggleAddModal}
            handleNewClick={handleNewClick}
            handleSubmit={handleSubmit}
            errorMsg={errorMsg}
          >
            {modalContent}
          </BtnModal>
        )}
      </Col>
    </Row>
  );
};
